import {
  Button,
  Stack,
  Text,
  Group,
  Alert,
  Checkbox,
  Select,
} from "@mantine/core"
import { useState, useMemo } from "react"
import { t } from "i18next"
import { notifications } from "@mantine/notifications"
import { getFormattedDate } from "@/utils/get-formatted-date"
import type {
  TheasoftActivity,
  TixlyEvent,
} from "@/@client/Theasoft_ApiServer_ProductionRoyalties"
import { usePostMutation } from "../../../../../typed-client/hooks/use-post-mutation"
import { DefaultModal } from "@components/default-modal"

interface LinkMappingModalProps {
  opened: boolean
  onClose: () => void
  activity: TheasoftActivity | null
  tixlyEvent: TixlyEvent | null
  allActivitiesOnDate: TheasoftActivity[]
  allEventsOnDate: TixlyEvent[]
  mappedEventIds: Set<number>
  onSuccess: () => void
}

export function LinkMappingModal({
  opened,
  onClose,
  activity,
  tixlyEvent,
  allActivitiesOnDate,
  allEventsOnDate,
  mappedEventIds,
  onSuccess,
}: LinkMappingModalProps) {
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
    null
  )
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [addToExternalLocationIds, setAddToExternalLocationIds] =
    useState(false)
  const { mutateAsync: updateExternalIds, isPending: isUpdatingIds } =
    usePostMutation("/api/ActicitySale/UpdateExternalIds")
  const { mutateAsync: addHallId, isPending: isAddingHallId } = usePostMutation(
    "/api/ActicitySale/AddHallIdToExternalLocationIds"
  )

  const candidateEvents = useMemo(() => {
    if (!activity) return []
    return allEventsOnDate.filter((e) => !mappedEventIds.has(e.eventId))
  }, [activity, allEventsOnDate, mappedEventIds])

  const candidateActivities = useMemo(() => {
    if (!tixlyEvent) return []
    return allActivitiesOnDate
  }, [tixlyEvent, allActivitiesOnDate])

  const finalActivity =
    activity ||
    (selectedActivityId
      ? allActivitiesOnDate.find(
          (a) => a.activityId === parseInt(selectedActivityId)
        )
      : null)
  const finalEvent =
    tixlyEvent ||
    (selectedEventId
      ? allEventsOnDate.find((e) => e.eventId === parseInt(selectedEventId))
      : null)

  const hasLocationMismatch =
    finalActivity &&
    finalEvent &&
    finalActivity.locationId !== finalEvent.hallId

  const handleLink = async () => {
    if (!finalActivity || !finalEvent) {
      notifications.show({
        title: t("Error"),
        message: t("SelectActivityAndEvent"),
        color: "red",
      })
      return
    }

    try {
      console.log("Starting link operation", {
        activityId: finalActivity.activityId,
        externalActivityId: finalEvent.eventId,
        hasLocationMismatch,
        addToExternalLocationIds,
      })

      await updateExternalIds([
        {
          activityId: finalActivity.activityId,
          externalActivityId: finalEvent.eventId,
        },
      ])

      console.log("UpdateExternalIds successful")

      if (hasLocationMismatch && addToExternalLocationIds) {
        console.log("Adding HallId to ExternalLocationIds", {
          locationId: finalActivity.locationId,
          hallId: finalEvent.hallId,
        })
        await addHallId({
          locationId: finalActivity.locationId,
          hallId: finalEvent.hallId,
        })
        console.log("AddHallId successful")
      }

      notifications.show({
        title: t("Success"),
        message: t("LinkSuccess"),
        color: "green",
      })

      onSuccess()
      onClose()
    } catch (error) {
      console.error("Fehler beim Verknüpfen:", error)
      notifications.show({
        title: t("LinkFailed"),
        message:
          error instanceof Error ? error.message : t("LinkFailedUnexpected"),
        color: "red",
      })
    }
  }

  const isLoading = isUpdatingIds || isAddingHallId

  const formatEventTime = (value?: string | null) => {
    if (!value) return ""
    const [hour = "", minute = ""] = value.split(":")
    return `${hour}:${minute}`
  }

  return (
    <DefaultModal
      opened={opened}
      onClose={onClose}
      title={t("LinkActivity")}
      size="lg"
    >
      <Stack gap="md">
        <div>
          <Text fw={600} size="sm" mb="xs">
            {t("DatabaseActivity")}
          </Text>
          {activity ? (
            <div>
              <Text size="sm">
                {getFormattedDate(activity.date)} | {activity.startTime}
              </Text>
              <Text size="sm">{activity.title}</Text>
              <Text size="sm" c="dimmed">
                {t("Location")}: {activity.location}
              </Text>
            </div>
          ) : candidateActivities.length > 0 ? (
            <Select
              placeholder={t("SelectActivity")}
              data={candidateActivities.map((a) => ({
                label: `${getFormattedDate(a.date)} | ${a.startTime} - ${a.title}`,
                value: a.activityId.toString(),
              }))}
              value={selectedActivityId}
              onChange={setSelectedActivityId}
            />
          ) : (
            <Text size="sm" c="dimmed">
              {t("NoActivitiesOnThisDay")}
            </Text>
          )}
        </div>

        <Text ta="center" fw={600}>
          {t("With")}
        </Text>

        <div>
          <Text fw={600} size="sm" mb="xs">
            {t("TixlyEvent")}
          </Text>
          {tixlyEvent ? (
            <div>
              <Text size="sm">
                {getFormattedDate(tixlyEvent.date)} |{" "}
                {formatEventTime(tixlyEvent.startTime)}
              </Text>
              <Text size="sm">{tixlyEvent.title}</Text>
              <Text size="sm" c="dimmed">
                {t("HallName")}: {tixlyEvent.hallName}
              </Text>
            </div>
          ) : candidateEvents.length > 0 ? (
            <Select
              placeholder={t("SelectTixlyEvent")}
              data={candidateEvents.map((e) => ({
                label: `${getFormattedDate(e.date)} | ${formatEventTime(e.startTime)} - ${e.title} (${e.hallName})`,
                value: e.eventId.toString(),
              }))}
              value={selectedEventId}
              onChange={setSelectedEventId}
            />
          ) : (
            <Text size="sm" c="dimmed">
              {t("NoUnlinkedEventsOnThisDay")}
            </Text>
          )}
        </div>

        {hasLocationMismatch && (
          <Alert title={t("DifferentVenues")} color="orange" icon={<>⚠️</>}>
            <Stack gap="sm">
              <Text size="sm">
                {t("LocationIdMismatch", {
                  locationId: finalActivity?.locationId,
                  hallId: finalEvent?.hallId,
                })}
              </Text>
              <Checkbox
                label={t("AddHallIdToExternalLocationIds")}
                checked={addToExternalLocationIds}
                onChange={(e) =>
                  setAddToExternalLocationIds(e.currentTarget.checked)
                }
              />
            </Stack>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isLoading}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleLink}
            loading={isLoading}
            color="blue"
            disabled={!finalActivity || !finalEvent}
          >
            {t("Link")}
          </Button>
        </Group>
      </Stack>
    </DefaultModal>
  )
}
